import type { WorkspaceSeed } from "@/domain/types";
import type { BriefingAlert, RailSeverity } from "@/design/components";
import { formatRelativeShort } from "@/design/format";

// On the surfaces where the Briefing IS the attention queue (programme / project /
// sub-project), the rail's action header and collapsed badge take their tint from the
// loudest alert. serious → bad, attention → warn, info → info; no alerts → neutral.
const ALERT_RANK = { serious: 3, attention: 2, info: 1 } as const;
const ALERT_TO_RAIL: Record<BriefingAlert["severity"], RailSeverity> = {
  serious: "bad",
  attention: "warn",
  info: "info",
};

export function railSeverityOfAlerts(alerts: BriefingAlert[]): RailSeverity {
  let top: BriefingAlert["severity"] | undefined;
  for (const a of alerts) {
    if (!top || ALERT_RANK[a.severity] > ALERT_RANK[top]) top = a.severity;
  }
  return top ? ALERT_TO_RAIL[top] : "neutral";
}

// Derive a small set of decision-oriented briefing alerts from real signals, scoped to a
// programme (projectIds) or a single sub-project. Shared by every rail so the analyst
// briefing reads the same everywhere. Actions are built by the caller (route vs tab) via
// makeAction, and money is pre-bound to the caller's persona currency.
export type BriefingScope = { projectIds?: string[]; subProjectIds?: string[] };
export type BriefingActionKind = "pending" | "findings" | "evidence";

export function deriveBriefingAlerts(
  seed: WorkspaceSeed,
  scope: BriefingScope,
  money: (n: number) => string,
  makeAction?: (kind: BriefingActionKind, label: string) => BriefingAlert["action"],
): BriefingAlert[] {
  const inScope = (r: { projectId?: string; subProjectId?: string }) => {
    if (scope.subProjectIds) return !!r.subProjectId && scope.subProjectIds.includes(r.subProjectId);
    if (scope.projectIds) return !!r.projectId && scope.projectIds.includes(r.projectId);
    return true;
  };
  const act = (k: BriefingActionKind, label: string) => (makeAction ? makeAction(k, label) : undefined);

  const pending = seed.spendRequests.filter((r) => (r.state === "proposed" || r.state === "flagged") && inScope(r));
  const oldest = pending.reduce<string>((o, r) => (!o || r.submittedAt < o ? r.submittedAt : o), "");
  const scopeAskIds = new Set(seed.spendRequests.filter(inScope).map((r) => r.id));
  const openFindings = seed.aiFindings.filter((f) => f.status === "open" && f.subjectAskId && scopeAskIds.has(f.subjectAskId));

  let spent = 0;
  let evidenced = 0;
  if (scope.subProjectIds) {
    const subs = seed.subProjects.filter((s) => scope.subProjectIds!.includes(s.id));
    spent = subs.reduce((a, s) => a + s.spent, 0);
    evidenced = subs.reduce((a, s) => a + s.evidenced, 0);
  } else {
    const projs = scope.projectIds ? seed.projects.filter((p) => scope.projectIds!.includes(p.id)) : seed.projects;
    spent = projs.reduce((a, p) => a + p.spent, 0);
    evidenced = projs.reduce((a, p) => a + p.evidenced, 0);
  }
  const unevidenced = Math.max(0, spent - evidenced);

  const alerts: BriefingAlert[] = [];
  // TODO(heimdall): AI analyst not built yet — re-enable the Heimdall findings briefing alert.
  // if (openFindings.length > 0)
  //   alerts.push({
  //     id: "findings",
  //     severity: openFindings.some((f) => f.severity === "serious") ? "serious" : "attention",
  //     title: `Heimdall has ${openFindings.length} open finding${openFindings.length > 1 ? "s" : ""}`,
  //     detail: "Each cites evidence and needs a person to decide.",
  //     action: act("findings", "Review findings"),
  //   });
  if (pending.length > 0)
    alerts.push({
      id: "pending",
      severity: "attention",
      title: `${pending.length} spend ask${pending.length > 1 ? "s" : ""} awaiting approval`,
      detail: oldest ? `Oldest submitted ${formatRelativeShort(oldest)} ago.` : undefined,
      action: act("pending", "Open approvals"),
    });
  if (unevidenced > 0)
    alerts.push({
      id: "evidence",
      severity: "info",
      title: `Evidence gap: ${money(unevidenced)} not yet evidenced`,
      detail: `${Math.round((evidenced / Math.max(1, spent)) * 100)}% of spend evidenced so far.`,
      action: act("evidence", "View evidence"),
    });
  return alerts;
}
